/* global ha */

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {SearchForm} from '@homeaway/react-search-form';
import {Waypoint} from '@homeaway/react-unveil';
import {logExperiment} from '@homeaway/abacus-client-utils';
import determineIfMobile from '@homeaway/react-determine-if-mobile';

import {setSearchData, redirect} from '../../../actions/search';
import {getSearchValues} from '../../../utils/search';
import {TESTS} from '../../../utils/abacus';

import {
    getOptions,
    getSettings,
    getStores,
    getTranslations,
    getValues,
    getElementHeight,
} from './utils/search-form';

import './styles.less';

export function mapStateToProps({
    abacus,
    config,
    destination,
    modules,
    search,
}) {
    const {
        site: {basePath},
    } = config;

    const {
        searchbox: {labels, resetForm},
    } = modules;

    const {
        templateType: {isMultiDestination},
    } = config;

    return {
        abacus,
        basePath,
        resetForm,
        isMultiDestination,
        ...getOptions(config, config.site.locale),
        ...getSettings(config),
        ...getStores(config, abacus),
        ...getTranslations(labels),
        ...getValues(destination, search),
    };
}

export class SearchFormComponent extends Component {
    static propTypes = {
        resetForm: PropTypes.bool,
        systemOptions: PropTypes.shape({
            site: PropTypes.string,
            baseUrl: PropTypes.string,
            appPrefix: PropTypes.string,
            locale: PropTypes.string,
        }),
        mediaSettings: PropTypes.shape({
            isMobile: PropTypes.bool,
        }),
        edapOptions: PropTypes.shape({
            searchQuerySubmittedEventActionLocation: PropTypes.string,
        }),
        formOptions: PropTypes.shape({
            layout: PropTypes.oneOf(['standard', 'feature']),
        }),
        searchValues: PropTypes.shape({
            filters: PropTypes.arrayOf(PropTypes.number),
        }),
        destinationValues: PropTypes.shape({
            destination: PropTypes.string,
            destinationUuid: PropTypes.string,
        }),
        datesOptions: PropTypes.objectOf(PropTypes.object),
        datesSettings: PropTypes.shape({
            isOpen: PropTypes.bool,
        }),
        datesValues: PropTypes.shape({
            startDate: PropTypes.string,
            endDate: PropTypes.string,
        }),
        guestsValues: PropTypes.shape({
            adultsCount: PropTypes.number,
            childrenCount: PropTypes.number,
            petIncluded: PropTypes.bool,
        }),
        guestsOptions: PropTypes.shape({
            actionLocation: PropTypes.string,
        }),
        translations: PropTypes.objectOf(PropTypes.string),
    };

    static defaultProps = {
        resetForm: false,
    };

    constructor(props) {
        super(props);

        this.state = {
            isOpen: false,
            rootMargin: '0px 0px 0px 0px',
        };

        this.searchFormReference = React.createRef();
    }

    componentDidMount() {
        ha.userTiming.mark('primary.action.rendered');
        ha.userTiming.mark('datepicker.active');

        this.setIsOpen();
        this.setRootMargin();
        this.checkSearchData();
    }

    setIsOpen = () => {
        if (
            !this.state.isOpen &&
            !this.props.isMobile &&
            !this.props.isMultiDestination
        ) {
            this.setState({
                isOpen: true,
            });
        }
    };

    setRootMargin = () => {
        if (this.props.hasTouch) {
            this.setState({
                rootMargin: `${getElementHeight(
                    this.searchFormReference
                )} 0px 0px 0px`,
            });
        }
    };

    checkSearchData = () => {
        const cookies = document.cookie;
        const {abacus, datesValues, guestsValues, dispatch} = this.props;

        const searchCookies = getSearchValues(cookies, abacus);

        if (!searchCookies || !(datesValues || guestsValues)) {
            return;
        }

        const {startDate: arrivalDate, endDate: departureDate} = datesValues;

        const {
            adultsCount: adults,
            childrenCount: children,
            petIncluded,
        } = guestsValues;

        const searchValues = {
            arrivalDate,
            departureDate,
            adults,
            children,
            petIncluded,
        };

        const differentSearchData = [
            'arrivalDate',
            'departureDate',
            'adults',
            'children',
            'petIncluded',
        ].some(
            (property) => searchValues[property] !== searchCookies[property]
        );

        if (differentSearchData) {
            dispatch(setSearchData(searchCookies));
        }
    };

    handleLeave = () => {
        logExperiment(TESTS.VRBO_LANDING_STICKY_SEARCHFORM);
    };

    onSubmit = ({searchPath}) => {
        const redirectPath = `${this.props.basePath || ''}${searchPath}`;
        this.props.dispatch(redirect(redirectPath));
    };

    render = () => {
        const {onSubmit} = this;

        const {
            resetForm,
            systemOptions,
            mediaSettings,
            abacusStore,
            edapOptions,
            formOptions,
            searchValues,
            datesOptions,
            datesValues,
            guestsValues,
            guestsOptions,
            translations,
            stickySearchFormDataOverrides,
        } = this.props;

        const datesSettings = {isOpen: this.state.isOpen};

        let {destinationValues} = this.props;

        if (resetForm) {
            destinationValues = {};
        }

        const formEvents = {
            onSubmit,
        };

        return (
            <React.Fragment>
                <Waypoint
                    label="searchform-waypoint"
                    onLeave={this.handleLeave}
                    rootMargin={this.state.rootMargin}
                    key={this.state.rootMargin === '0px 0px 0px 0px'}
                />
                <div ref={this.searchFormReference}>
                    <SearchForm
                        systemOptions={systemOptions}
                        mediaSettings={mediaSettings}
                        abacusStore={abacusStore}
                        edapOptions={edapOptions}
                        searchValues={searchValues}
                        formEvents={formEvents}
                        formOptions={formOptions}
                        destinationValues={destinationValues}
                        datesOptions={datesOptions}
                        datesSettings={datesSettings}
                        datesValues={datesValues}
                        guestsOptions={guestsOptions}
                        guestsValues={guestsValues}
                        translations={translations}
                        {...stickySearchFormDataOverrides}
                    />
                </div>
            </React.Fragment>
        );
    };
}

export const SearchFormContainer = connect(mapStateToProps)(
    determineIfMobile(SearchFormComponent)
);

SearchFormContainer.displayName = 'SearchFormContainer';

export default SearchFormContainer;
